import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Camera, CameraOff } from "lucide-react";
import {
  BarcodeFormat,
  BrowserMultiFormatReader,
} from "@zxing/browser";
import {
  clearCameraGrantedForUser,
  isCameraGrantedForUser,
  markCameraGrantedForUser,
  queryBrowserCameraPermission,
  shouldAutoStartCamera,
} from "../services/cameraPermission";
import {
  attachStreamToVideo,
  isBusyCameraError,
  isCameraApiAvailable,
  isNotAllowedError,
  isStreamLive,
  openCameraStream,
  stopMediaStream,
} from "../services/cameraStream";
import { maxPlatform } from "../services/maxPlatform";
import { decodeScanCodeFromImage } from "../services/decodeBarcodeFromImage";
import { debugLog } from "../services/debugLog";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const SCAN_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.ITF,
];

/** Login badge: 1D only — avoid Cabex page QRs (Telegram / site). */
const BADGE_SCAN_FORMATS = [
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.ITF,
];

function symbologyToCodeType(format: BarcodeFormat): "qr" | "barcode" {
  return format === BarcodeFormat.QR_CODE ? "qr" : "barcode";
}

export interface CodeScanResult {
  text: string;
  codeType: "qr" | "barcode";
}

type DecodeControls = { stop: () => void };

interface CodeScannerProps {
  /** Persist grant / auto-start per visitor. */
  userId?: string | null;
  /** Keep camera stream open while true (entire time on Scan page). */
  active?: boolean;
  /** Pause decode callbacks without stopping the camera stream. */
  paused?: boolean;
  /** Layout hint from callers. */
  variant?: "default" | "badge" | "scan";
  /**
   * Login / first grant: never auto-start — always show Allow and call
   * getUserMedia only from a tap (required on iOS / Android / PWA).
   */
  requireUserGesture?: boolean;
  /**
   * Stream already opened in a user-gesture click handler (login page).
   * When set, skip permission gate and attach this stream.
   */
  initialStream?: MediaStream | null;
  onScan: (result: CodeScanResult) => void;
  className?: string;
}

/**
 * Preview: we own &lt;video&gt; + MediaStream.
 * Decode: canvas snapshots — never ZXing decodeFromStream (it steals the
 * video element and leaves a black preview while the LED stays on).
 */
export function CodeScanner({
  userId = null,
  active = true,
  paused = false,
  variant = "default",
  requireUserGesture = false,
  initialStream = null,
  onScan,
  className = "aspect-[4/3]",
}: CodeScannerProps) {
  const badgeMode = variant === "badge";
  const compactChrome = variant === "badge" || variant === "scan";
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const controlsRef = useRef<DecodeControls | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const teardownEpochRef = useRef(0);
  const sessionRef = useRef(0);
  const lastScanRef = useRef<{ text: string; at: number } | null>(null);
  const onScanRef = useRef(onScan);
  const pausedRef = useRef(paused);
  const startingLockRef = useRef(false);
  const startingSinceRef = useRef(0);
  const allowTapGuardRef = useRef(0);
  /** After native MAX QR reader cancel/empty — next tap uses photo capture (QR+barcode). */
  const maxPreferCaptureRef = useRef(false);
  const maxCaptureRef = useRef<HTMLInputElement | null>(null);
  const requireGestureRef = useRef(requireUserGesture);
  const initialStreamRef = useRef(initialStream);
  const startWithStreamRef = useRef<(stream: MediaStream, session: number) => Promise<void>>(
    async () => {},
  );
  /** false when parent handed initialStream — parent stops tracks on leave. */
  const ownsStreamRef = useRef(!initialStream);

  const [cameraError, setCameraError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [needsTap, setNeedsTap] = useState(requireUserGesture && !initialStream);
  const [hardDenied, setHardDenied] = useState(false);
  const [ready, setReady] = useState(requireUserGesture || Boolean(initialStream));
  const [live, setLive] = useState(false);
  /** MAX: after first camera use, CTA is «Сканировать», not «Предоставить доступ». */
  const [maxScanReady, setMaxScanReady] = useState(() => {
    try {
      return sessionStorage.getItem("mvk_max_scan_ready") === "1";
    } catch {
      return false;
    }
  });

  const markMaxScanReady = useCallback(() => {
    setMaxScanReady(true);
    try {
      sessionStorage.setItem("mvk_max_scan_ready", "1");
    } catch {
      /* ignore */
    }
  }, []);

  requireGestureRef.current = requireUserGesture;
  initialStreamRef.current = initialStream;

  onScanRef.current = onScan;
  pausedRef.current = paused;

  const cancelScheduledStop = useCallback(() => {
    teardownEpochRef.current += 1;
    if (stopTimerRef.current != null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }, []);

  const waitForVideoNode = useCallback(async (timeoutMs = 2000) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      if (videoRef.current) return videoRef.current;
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
    }
    return videoRef.current;
  }, []);

  const tearDown = useCallback(
    (immediate: boolean) => {
      const epoch = ++teardownEpochRef.current;
      const run = () => {
        // Superseded by a newer start / remount — do not clear the live preview.
        if (epoch !== teardownEpochRef.current) return;
        try {
          controlsRef.current?.stop();
        } catch {
          /* ignore */
        }
        controlsRef.current = null;
        if (ownsStreamRef.current) {
          stopMediaStream(streamRef.current);
        }
        streamRef.current = null;
        const video = videoRef.current;
        if (video) video.srcObject = null;
        setLive(false);
      };

      if (stopTimerRef.current != null) {
        window.clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      if (immediate) {
        run();
        return;
      }
      // React 18 Strict Mode remounts in ~ms; keep the hardware session.
      stopTimerRef.current = window.setTimeout(run, 480);
    },
    [],
  );

  const startDecodeLoop = useCallback((video: HTMLVideoElement, session: number): DecodeControls => {
    const decodeCanvas = document.createElement("canvas");
    const decodeCtx = decodeCanvas.getContext("2d", { willReadFrequently: true });
    const formats = badgeMode ? BADGE_SCAN_FORMATS : SCAN_FORMATS;
    // DecodeHintType.POSSIBLE_FORMATS=2, TRY_HARDER=3 — helps thin EAN on phone screens.
    const hints = new Map<number, unknown>([
      [2, formats],
      [3, true],
    ]);
    const reader = new BrowserMultiFormatReader(hints as never);
    reader.possibleFormats = formats;
    let timer: number | null = null;
    let stopped = false;
    let frameLogOnce = false;

    const emit = (text: string, format: BarcodeFormat) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const now = Date.now();
      const last = lastScanRef.current;
      if (last && last.text === trimmed && now - last.at < 2500) return;
      lastScanRef.current = { text: trimmed, at: now };
      onScanRef.current({
        text: trimmed,
        codeType: symbologyToCodeType(format),
      });
    };

    const paintPreviewCover = (
      preview: HTMLCanvasElement,
      previewCtx: CanvasRenderingContext2D,
      srcW: number,
      srcH: number,
    ) => {
      const host = preview.parentElement;
      const cssW = Math.max(
        1,
        preview.clientWidth || host?.clientWidth || Math.round(preview.getBoundingClientRect().width),
      );
      const cssH = Math.max(
        1,
        preview.clientHeight || host?.clientHeight || Math.round(preview.getBoundingClientRect().height),
      );
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const dstW = Math.max(1, Math.round(cssW * dpr));
      const dstH = Math.max(1, Math.round(cssH * dpr));
      if (preview.width !== dstW || preview.height !== dstH) {
        preview.width = dstW;
        preview.height = dstH;
      }
      const scale = Math.max(dstW / srcW, dstH / srcH);
      const dw = srcW * scale;
      const dh = srcH * scale;
      const dx = (dstW - dw) / 2;
      const dy = (dstH - dh) / 2;
      previewCtx.fillStyle = "#000";
      previewCtx.fillRect(0, 0, dstW, dstH);
      previewCtx.drawImage(video, dx, dy, dw, dh);
    };

    const tick = () => {
      if (stopped || session !== sessionRef.current) return;
      const preview = previewCanvasRef.current;
      const previewCtx = preview?.getContext("2d", { alpha: false }) ?? null;

      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w > 0 && h > 0) {
          if (preview && previewCtx) {
            try {
              paintPreviewCover(preview, previewCtx, w, h);
              if (!frameLogOnce) {
                frameLogOnce = true;
                debugLog.info("camera", "preview canvas cover-fill", {
                  videoW: w,
                  videoH: h,
                  canvasW: preview.width,
                  canvasH: preview.height,
                  cssW: preview.clientWidth,
                  cssH: preview.clientHeight,
                });
              }
            } catch {
              /* ignore draw errors */
            }
          }

          if (!pausedRef.current && decodeCtx) {
            if (decodeCanvas.width !== w || decodeCanvas.height !== h) {
              decodeCanvas.width = w;
              decodeCanvas.height = h;
            }
            try {
              decodeCtx.drawImage(video, 0, 0, w, h);
              const result = reader.decodeFromCanvas(decodeCanvas);
              if (result) {
                emit(result.getText(), result.getBarcodeFormat());
              }
            } catch {
              // NotFoundException / checksum — normal while aiming.
            }
          }
        }
      }
      timer = window.setTimeout(tick, 66);
    };

    tick();
    return {
      stop: () => {
        stopped = true;
        if (timer != null) window.clearTimeout(timer);
        timer = null;
      },
    };
  }, [badgeMode]);

  const startWithStream = useCallback(
    async (stream: MediaStream, session: number) => {
      const node = await waitForVideoNode();
      if (!node || session !== sessionRef.current) {
        // Never stop a parent-owned login stream just because video is late.
        if (ownsStreamRef.current && stream !== initialStreamRef.current) {
          stopMediaStream(stream);
        }
        if (session === sessionRef.current) {
          setStarting(false);
          setLive(false);
          setNeedsTap(true);
          setCameraError(t("visitor.camera_open_failed"));
        }
        return;
      }

      cancelScheduledStop();
      if (streamRef.current && streamRef.current !== stream && ownsStreamRef.current) {
        stopMediaStream(streamRef.current);
      }
      streamRef.current = stream;

      try {
        controlsRef.current?.stop();
      } catch {
        /* ignore */
      }
      controlsRef.current = null;

      await attachStreamToVideo(node, stream);
      if (session !== sessionRef.current) return;

      // Keep preview on OUR video element — do not hand it to ZXing.
      markCameraGrantedForUser(userId);
      setNeedsTap(false);
      setHardDenied(false);
      setCameraError(null);
      setStarting(false);
      setLive(true);
      debugLog.info("camera", "stream live", {
        userId: userId ?? null,
        w: node.videoWidth,
        h: node.videoHeight,
        readyState: node.readyState,
      });

      controlsRef.current = startDecodeLoop(node, session);
    },
    [cancelScheduledStop, startDecodeLoop, t, userId, waitForVideoNode],
  );

  startWithStreamRef.current = startWithStream;

  const startCameraRef = useRef<(fromGesture: boolean) => Promise<void>>(async () => {});

  const startCamera = useCallback(
    async (fromGesture: boolean) => {
      if (!active) return;
      if (startingLockRef.current) return;
      if (isStreamLive(streamRef.current) && live) {
        setNeedsTap(false);
        setStarting(false);
        setLive(true);
        const video = videoRef.current;
        if (video && streamRef.current) void video.play().catch(() => undefined);
        return;
      }

      if (!isCameraApiAvailable()) {
        setHardDenied(false);
        setNeedsTap(true);
        setCameraError(t("visitor.camera_open_failed"));
        return;
      }

      startingLockRef.current = true;
      const session = ++sessionRef.current;
      setStarting(true);
      setCameraError(null);

      try {
        cancelScheduledStop();
        const existing = isStreamLive(streamRef.current) ? streamRef.current : null;
        if (!existing) ownsStreamRef.current = true;
        const stream = existing ?? (await openCameraStream({ timeoutMs: 10000 }));
        await startWithStream(stream, session);
      } catch (err) {
        if (session !== sessionRef.current) return;
        setStarting(false);
        setLive(false);
        const denied = isNotAllowedError(err);
        const busy = isBusyCameraError(err);
        const timedOut =
          err instanceof Error && /camera_timeout/i.test(err.message);
        debugLog.warn("camera", "start failed", {
          denied,
          busy,
          timedOut,
          fromGesture,
          name: err instanceof Error ? err.name : undefined,
          message: err instanceof Error ? err.message : String(err),
        });

        if (denied) {
          const browser = await queryBrowserCameraPermission();
          setNeedsTap(true);
          if (browser === "denied" && !maxPlatform.isAvailable()) {
            setHardDenied(true);
            clearCameraGrantedForUser(userId);
            setCameraError(t("visitor.camera_settings_hint"));
          } else if (maxPlatform.isAvailable()) {
            // Soft deny / WebView prompt: keep one-tap gate; do not open photo without gesture.
            setHardDenied(false);
            setCameraError(null);
          } else {
            setHardDenied(false);
            setCameraError(t("visitor.camera_settings_hint"));
          }
        } else {
          setNeedsTap(true);
          setCameraError(
            busy
              ? t("visitor.camera_busy")
              : timedOut
                ? t("visitor.camera_open_failed")
                : t("visitor.camera_open_failed"),
          );
        }
      } finally {
        startingLockRef.current = false;
      }
    },
    [active, cancelScheduledStop, live, startWithStream, t, userId],
  );

  startCameraRef.current = startCamera;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!active) return;

      const handed = initialStreamRef.current;
      if (handed && isStreamLive(handed)) {
        ownsStreamRef.current = false;
        setNeedsTap(false);
        setHardDenied(false);
        setReady(true);
        setStarting(true);
        const session = ++sessionRef.current;
        try {
          await startWithStreamRef.current(handed, session);
        } catch (err) {
          if (cancelled) return;
          debugLog.warn("camera", "initialStream attach failed", {
            message: err instanceof Error ? err.message : String(err),
          });
          setStarting(false);
          setNeedsTap(true);
          setCameraError(t("visitor.camera_open_failed"));
        }
        return;
      }

      if (requireUserGesture) {
        setNeedsTap(true);
        setReady(true);
        setHardDenied(false);
        return;
      }
      // MAX + PWA: same auto-start rules. Live getUserMedia when already granted;
      // first visit still needs one tap (OS permission prompt requires gesture).
      const browser = await queryBrowserCameraPermission();
      if (cancelled) return;
      if (browser === "denied" && !maxPlatform.isAvailable()) {
        setHardDenied(true);
        setNeedsTap(true);
        setReady(true);
        return;
      }
      const auto = await shouldAutoStartCamera(userId);
      if (cancelled) return;
      setHardDenied(false);
      setNeedsTap(!auto);
      setReady(true);
      if (auto) void startCameraRef.current(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [active, requireUserGesture, initialStream, t, userId]);

  useEffect(() => {
    if (!active) {
      // Leave scan / pause parent: stop LED and tracks immediately (IDEA-048).
      tearDown(true);
      return;
    }
    return () => {
      // Real route leave: stop now. Strict Mode remount restarts within ms.
      tearDown(variant === "scan" || variant === "badge" ? true : false);
    };
  }, [active, tearDown, variant]);

  useEffect(() => {
    const resume = () => {
      if (!active || document.visibilityState !== "visible") return;
      if (isStreamLive(streamRef.current)) {
        void videoRef.current?.play().catch(() => undefined);
        return;
      }
      // Do not auto getUserMedia on focus — iOS PWA re-prompts every time.
      // User taps "Allow / Retry" on the gate instead.
    };
    const onPageShow = (ev: PageTransitionEvent) => {
      if (ev.persisted) resume();
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [active]);

  const onMaxCaptureChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0] ?? null;
      ev.target.value = "";
      if (!file) {
        setStarting(false);
        markMaxScanReady();
        return;
      }
      setStarting(true);
      setCameraError(null);
      setHardDenied(false);
      markMaxScanReady();
      markCameraGrantedForUser(userId);
      void (async () => {
        try {
          const decoded = await decodeScanCodeFromImage(file);
          maxPreferCaptureRef.current = false;
          onScanRef.current(decoded);
        } catch (err) {
          debugLog.warn("camera", "max capture decode failed", {
            message: err instanceof Error ? err.message : String(err),
          });
          const hint = t("visitor.camera_max_not_recognized");
          setCameraError(hint);
          setNeedsTap(true);
          toast.error(hint);
        } finally {
          setStarting(false);
        }
      })();
    },
    [markMaxScanReady, t, userId],
  );

  const openMaxCapture = useCallback(() => {
    setHardDenied(false);
    setCameraError(null);
    setNeedsTap(true);
    setStarting(true);
    markMaxScanReady();
    const input = maxCaptureRef.current;
    if (!input) {
      setStarting(false);
      setCameraError(t("visitor.camera_open_failed"));
      return;
    }
    input.click();
    window.setTimeout(() => {
      if (!maxCaptureRef.current?.files?.length) setStarting(false);
    }, 1200);
  }, [markMaxScanReady, t]);

  const onAllowTap = useCallback(() => {
    const now = Date.now();
    if (now - allowTapGuardRef.current < 450) return;
    allowTapGuardRef.current = now;
    maxPlatform.hapticFeedback("light");

    /**
     * Unified path (PWA + MAX):
     * 1) Live getUserMedia + ZXing decode loop (QR + 1D barcodes) — same as PWA.
     * 2) MAX only: if WebView blocks continuous camera, fall back to one photo
     *    capture in the same gesture (still decodes QR + barcode).
     * Host openCodeReader is QR-only — never use it as the primary scanner.
     */
    if (!isCameraApiAvailable()) {
      if (maxPlatform.isAvailable()) {
        openMaxCapture();
        return;
      }
      setCameraError(t("visitor.camera_open_failed"));
      setNeedsTap(true);
      setHardDenied(false);
      return;
    }
    if (startingLockRef.current) {
      if (now - startingSinceRef.current < 8000) return;
      startingLockRef.current = false;
      sessionRef.current += 1;
    }

    setHardDenied(false);
    setCameraError(null);
    startingLockRef.current = true;
    startingSinceRef.current = now;
    const session = ++sessionRef.current;
    setStarting(true);
    cancelScheduledStop();
    ownsStreamRef.current = true;
    const gumPromise = openCameraStream();

    const timeoutId = window.setTimeout(() => {
      if (session !== sessionRef.current) return;
      sessionRef.current += 1;
      setStarting(false);
      setLive(false);
      startingLockRef.current = false;
      if (maxPlatform.isAvailable()) {
        debugLog.warn("camera", "allow tap timed out → max photo fallback");
        openMaxCapture();
        return;
      }
      setNeedsTap(true);
      setCameraError(t("visitor.camera_open_failed"));
      debugLog.warn("camera", "allow tap timed out");
    }, 10000);

    void (async () => {
      try {
        const stream = await gumPromise;
        if (session !== sessionRef.current) {
          stopMediaStream(stream);
          return;
        }
        window.clearTimeout(timeoutId);
        markMaxScanReady();
        await startWithStream(stream, session);
      } catch (err) {
        window.clearTimeout(timeoutId);
        if (session !== sessionRef.current) return;
        setStarting(false);
        setLive(false);
        debugLog.warn("camera", "allow tap failed", {
          name: err instanceof Error ? err.name : undefined,
          message: err instanceof Error ? err.message : String(err),
          inMax: maxPlatform.isAvailable(),
        });

        // MAX: photo capture still works when continuous WebView camera is blocked.
        if (maxPlatform.isAvailable()) {
          openMaxCapture();
          return;
        }

        setNeedsTap(true);
        if (isNotAllowedError(err)) {
          const browser = await queryBrowserCameraPermission();
          setHardDenied(browser === "denied");
          if (browser === "denied") clearCameraGrantedForUser(userId);
          setCameraError(t("visitor.camera_settings_hint"));
        } else {
          setCameraError(
            isBusyCameraError(err) ? t("visitor.camera_busy") : t("visitor.camera_open_failed"),
          );
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (session === sessionRef.current) startingLockRef.current = false;
      }
    })();
  }, [cancelScheduledStop, markMaxScanReady, openMaxCapture, startWithStream, t, userId]);

  const setVideoNode = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
  }, []);

  const setPreviewCanvas = useCallback((node: HTMLCanvasElement | null) => {
    previewCanvasRef.current = node;
  }, []);

  const showGate = ready && active && (needsTap || hardDenied || Boolean(cameraError)) && !live;
  const inMax = maxPlatform.isAvailable();

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gray-900 ${className}`}>
      <input
        ref={maxCaptureRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="pointer-events-none absolute h-0 w-0 opacity-0"
        tabIndex={-1}
        aria-hidden
        onChange={onMaxCaptureChange}
      />
      {/* Capture source — keep full layout size; paint preview on canvas above
          (Edge/Android often leave &lt;video&gt; visually black while frames decode). */}
      <video
        ref={setVideoNode}
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover opacity-0"
        muted
        playsInline
        autoPlay
        controls={false}
        disablePictureInPicture
      />
      <canvas
        ref={setPreviewCanvas}
        className={`absolute inset-0 z-0 block h-full w-full bg-black ${paused ? "opacity-60" : ""}`}
      />

      {/* Guides only — never a flex-col stack that reads as a second “dead” zone under the feed. */}
      <div className="pointer-events-none absolute inset-0 z-[1]">
        {badgeMode ? (
          <div className="absolute left-1/2 top-1/2 w-[90%] max-w-md h-28 -translate-x-1/2 -translate-y-1/2">
            <div className="absolute top-0 left-0 w-7 h-7 border-l-2 border-t-2 border-emerald-400 rounded-tl-sm" />
            <div className="absolute top-0 right-0 w-7 h-7 border-r-2 border-t-2 border-emerald-400 rounded-tr-sm" />
            <div className="absolute bottom-0 left-0 w-7 h-7 border-l-2 border-b-2 border-emerald-400 rounded-bl-sm" />
            <div className="absolute bottom-0 right-0 w-7 h-7 border-r-2 border-b-2 border-emerald-400 rounded-br-sm" />
            <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-medium text-white/90 tracking-wide whitespace-nowrap">
              {t("scan.frame_barcode")}
            </span>
          </div>
        ) : (
          <>
            <div className="absolute left-1/2 top-[28%] w-[58%] max-w-48 aspect-square -translate-x-1/2 -translate-y-1/2">
              <div className="absolute top-0 left-0 w-7 h-7 border-l-2 border-t-2 border-indigo-400 rounded-tl-sm" />
              <div className="absolute top-0 right-0 w-7 h-7 border-r-2 border-t-2 border-indigo-400 rounded-tr-sm" />
              <div className="absolute bottom-0 left-0 w-7 h-7 border-l-2 border-b-2 border-indigo-400 rounded-bl-sm" />
              <div className="absolute bottom-0 right-0 w-7 h-7 border-r-2 border-b-2 border-indigo-400 rounded-br-sm" />
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[11px] font-medium text-white/90 tracking-wide whitespace-nowrap">
                {t("scan.frame_qr")}
              </span>
            </div>
            <div className="absolute left-1/2 top-[68%] w-[78%] max-w-64 h-14 -translate-x-1/2 -translate-y-1/2">
              <div className="absolute top-0 left-0 w-7 h-7 border-l-2 border-t-2 border-emerald-400 rounded-tl-sm" />
              <div className="absolute top-0 right-0 w-7 h-7 border-r-2 border-t-2 border-emerald-400 rounded-tr-sm" />
              <div className="absolute bottom-0 left-0 w-7 h-7 border-l-2 border-b-2 border-emerald-400 rounded-bl-sm" />
              <div className="absolute bottom-0 right-0 w-7 h-7 border-r-2 border-b-2 border-emerald-400 rounded-br-sm" />
              <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[11px] font-medium text-white/90 tracking-wide whitespace-nowrap">
                {t("scan.frame_barcode")}
              </span>
            </div>
          </>
        )}
      </div>

      {showGate && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900/88 p-6 text-center">
          <div className="max-w-xs space-y-3">
            {cameraError && !inMax ? (
              <AlertCircle size={36} className="mx-auto text-red-400" />
            ) : (
              <Camera size={36} className="mx-auto text-indigo-300" />
            )}
            <p className="text-[15px] text-white font-medium">
              {inMax ? t("scan.title") : t("visitor.camera_access")}
            </p>
            <p className="text-[12px] text-white/75 leading-relaxed">
              {hardDenied && !inMax
                ? t("visitor.camera_settings_hint")
                : inMax
                  ? isCameraGrantedForUser(userId) || maxScanReady
                    ? t("visitor.camera_max_live_hint")
                    : t("visitor.camera_max_hint")
                  : t("visitor.camera_consent")}
            </p>
            {cameraError && !inMax && (
              <p className="text-[12px] text-red-200">{cameraError}</p>
            )}
            {cameraError && inMax && !live && (
              <p className="text-[12px] text-amber-200">{cameraError}</p>
            )}
            <button
              type="button"
              disabled={starting}
              onClick={onAllowTap}
              className="pointer-events-auto w-full rounded-xl bg-indigo-500 px-4 py-3 text-[14px] font-medium text-white active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {starting
                ? t("visitor.camera_requesting")
                : inMax
                  ? isCameraGrantedForUser(userId) || maxScanReady
                    ? t("scan.scan_action")
                    : t("scan.allow_camera")
                  : hardDenied || cameraError
                    ? t("scan.retry")
                    : isCameraGrantedForUser(userId)
                      ? t("scan.scan_action")
                      : t("scan.allow_camera")}
            </button>
            {inMax && (isCameraGrantedForUser(userId) || maxScanReady) && !starting ? (
              <button
                type="button"
                onClick={() => {
                  maxPlatform.hapticFeedback("light");
                  openMaxCapture();
                }}
                className="pointer-events-auto w-full text-[12px] text-white/60 underline-offset-2 hover:underline"
              >
                {t("visitor.camera_max_photo_fallback")}
              </button>
            ) : null}
          </div>
        </div>
      )}

      <div
        className={`absolute inset-x-0 bottom-0 z-[1] bg-gradient-to-t from-black/70 to-transparent text-center ${
          compactChrome ? "px-4 py-2.5" : "px-4 py-3"
        }`}
      >
        <p
          className={`text-white font-medium flex items-center justify-center gap-1.5 ${
            compactChrome ? "text-[12px]" : "text-[13px]"
          }`}
        >
          {cameraError ? (
            <>
              <CameraOff size={14} />
              {cameraError}
            </>
          ) : needsTap || hardDenied ? (
            <>
              <Camera size={14} />
              {inMax
                ? isCameraGrantedForUser(userId) || maxScanReady
                  ? t("scan.scan_action")
                  : t("scan.allow_camera")
                : t("visitor.camera_needed")}
            </>
          ) : starting ? (
            <>
              <Camera size={14} className="animate-pulse" />
              {isCameraGrantedForUser(userId)
                ? t("visitor.camera_connecting")
                : t("visitor.camera_requesting")}
            </>
          ) : paused ? (
            <>
              <Camera size={14} />
              {t("visitor.camera_paused")}
            </>
          ) : (
            <>
              <Camera size={14} />
              {badgeMode ? t("scan.aim_hint_badge") : t("scan.aim_hint")}
            </>
          )}
        </p>
        {!compactChrome && (
          <p className="text-[11px] text-white/70 mt-0.5">{t("visitor.scan_stands_badges_products")}</p>
        )}
      </div>
    </div>
  );
}
